import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, Download, Lock, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_META = {
  redundant: { label: "Redundant", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  needs_review: { label: "Needs review", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
};

// Retrospective identity-based duplicate sweep (term + parent + children).
// Read-only scan → admin confirms cancellation per group. Losers are archived
// (recoverable), never a paid/approved txn, never different-children regs.
export default function AdminDuplicateRegistrations() {
  const [terms, setTerms] = useState([]);
  const [selected, setSelected] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyGroup, setBusyGroup] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/academy/terms`, { withCredentials: true });
        const visible = (r.data || []).filter(
          (t) => !t.is_hidden && !t.is_archived && String(t.status || "").toLowerCase() !== "archived"
        );
        setTerms(visible);
        const pre = {};
        visible.forEach((t) => { if (/term\s*3/i.test(t.name || "")) pre[t.id] = true; });
        setSelected(pre);
      } catch { toast.error("Failed to load terms"); }
    })();
  }, []);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  const runScan = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one term"); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payments-hub/admin/duplicate-identity-scan`, {
        params: { term_ids: selectedIds.join(",") }, withCredentials: true,
      });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally { setLoading(false); }
  };

  const cancelGroup = async (g, gi) => {
    if (!window.confirm(
      `Cancel ${g.loser_ids.length} unpaid duplicate(s) for ${g.parent_name || g.parent_email} (${g.children})?\n\n` +
      `Keeper (paid/approved) stays. Losers are archived (recoverable), never deleted. Paid/approved transactions are never touched.`
    )) return;
    setBusyGroup(gi);
    try {
      const res = await axios.post(`${API}/payments-hub/admin/archive-duplicate-transactions`,
        { keeper_id: g.keeper_id, loser_ids: g.loser_ids, reason: "duplicate_registration" },
        { withCredentials: true });
      toast.success(res.data?.message || "Duplicates archived");
      await runScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally { setBusyGroup(null); }
  };

  const cancelAllRedundant = async () => {
    const redundant = (data?.groups || []).filter((g) => g.category === "redundant" && (g.loser_ids || []).length > 0);
    const totalLosers = redundant.reduce((n, g) => n + g.loser_ids.length, 0);
    if (totalLosers === 0) { toast.info("Nothing to archive"); return; }
    if (!window.confirm(
      `Archive ${totalLosers} unpaid duplicate(s) across ${redundant.length} group(s)?\n\n` +
      `Only groups with exactly ONE paid/approved keeper are touched. Losers are archived (recoverable), never deleted. Paid/approved transactions are never touched. "Needs review" groups are skipped.`
    )) return;
    setBusyAll(true);
    let ok = 0, fail = 0;
    for (const g of redundant) {
      try {
        await axios.post(`${API}/payments-hub/admin/archive-duplicate-transactions`,
          { keeper_id: g.keeper_id, loser_ids: g.loser_ids, reason: "duplicate_registration_bulk" },
          { withCredentials: true });
        ok += g.loser_ids.length;
      } catch { fail += g.loser_ids.length; }
    }
    setBusyAll(false);
    if (ok) toast.success(`Archived ${ok} duplicate(s)${fail ? `, ${fail} failed` : ""}`);
    else toast.error("Failed to archive duplicates");
    await runScan();
  };

  const exportCsv = () => {
    window.open(`${API}/payments-hub/admin/duplicate-identity-scan?term_ids=${encodeURIComponent(selectedIds.join(","))}&format=csv`, "_blank");
  };

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="admin-dup-registrations-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-to-admin-link">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-1">
          <Users className="w-7 h-7 text-amber-400" />
          <h1 className="text-3xl font-bold">Duplicate Registrations Sweep</h1>
        </div>
        <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 mb-4" data-testid="safety-banner">
          <Lock className="w-3 h-3" /> Scan is read-only. Cancellation is per-group, admin-confirmed, archived (recoverable) — never auto, never a paid txn.
        </div>
        <p className="text-slate-400 text-sm mb-6 max-w-4xl">
          Matches duplicates by human identity — same term + same parent (email, else name) + same set of children.
          Different children are never flagged. For each duplicate group you can cancel the unpaid copy while the
          paid/approved one stays.
        </p>

        <Card className="p-5 bg-slate-900/60 border border-white/10 rounded-xl mb-6">
          <label className="block text-sm text-slate-400 mb-2">Terms (default: Term 3)</label>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="term-picker">
            {terms.map((t) => (
              <button key={t.id} onClick={() => setSelected((s) => ({ ...s, [t.id]: !s[t.id] }))}
                data-testid={`term-chip-${t.id}`}
                className={`text-sm px-3 py-1.5 rounded-full border ${selected[t.id] ? "bg-cyan-500 text-black border-cyan-400 font-semibold" : "bg-slate-800 text-slate-300 border-white/10"}`}>
                {t.name}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <Button onClick={runScan} disabled={loading} data-testid="run-scan-btn" className="bg-cyan-600 hover:bg-cyan-500 text-white">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Run scan
            </Button>
            {data && (
              <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn" className="border-white/20">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            )}
          </div>
        </Card>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="scan-summary">
              <Card className="p-4 bg-slate-900/60 border border-white/10 rounded-xl">
                <div className="text-2xl font-bold">{data.summary.identities_scanned}</div>
                <div className="text-xs text-slate-400">Identities scanned</div>
              </Card>
              <Card className="p-4 bg-slate-900/60 border border-amber-500/30 rounded-xl">
                <div className="text-2xl font-bold text-amber-300" data-testid="redundant-count">{data.summary.redundant_count}</div>
                <div className="text-xs text-slate-400">Redundant groups</div>
              </Card>
              <Card className="p-4 bg-slate-900/60 border border-rose-500/30 rounded-xl">
                <div className="text-2xl font-bold text-rose-300">{data.summary.needs_review_count}</div>
                <div className="text-xs text-slate-400">Needs review</div>
              </Card>
              <Card className="p-4 bg-slate-900/60 border border-emerald-500/30 rounded-xl">
                <div className="text-2xl font-bold text-emerald-300" data-testid="cancel-amount">{money(data.summary.total_cancel_candidate_amount)}</div>
                <div className="text-xs text-slate-400">Cancel-candidate $</div>
              </Card>
            </div>

            {data.summary.redundant_count > 0 && (
              <Card className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="bulk-archive-bar">
                <div className="text-sm text-amber-200">
                  <strong>{data.summary.redundant_count}</strong> redundant group(s) — {money(data.summary.total_cancel_candidate_amount)} of unpaid duplicates can be archived in one go. The paid/approved copy is always kept; this is reversible.
                </div>
                <Button data-testid="archive-all-redundant-btn" disabled={busyAll}
                  onClick={cancelAllRedundant}
                  className="bg-rose-600 hover:bg-rose-500 text-white">
                  {busyAll ? "Archiving…" : `Archive all redundant (${data.summary.redundant_count})`}
                </Button>
              </Card>
            )}

            {data.groups.length === 0 ? (
              <Card className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3" data-testid="scan-clean">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                <span>No duplicate registrations found for the selected term(s).</span>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="group-list">
                {data.groups.map((g, gi) => {
                  const meta = CAT_META[g.category] || { label: g.category, cls: "bg-slate-700 text-slate-300" };
                  return (
                    <Card key={gi} data-testid={`group-${gi}`} className="p-4 bg-slate-900/60 border border-white/10 rounded-xl">
                      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                        <div>
                          <div className="font-semibold">{g.parent_name || g.parent_email || "Unknown parent"}</div>
                          <div className="text-xs text-slate-400">{g.children} · {g.term_name}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${meta.cls}`}>
                            {g.category === "redundant" ? <AlertTriangle className="w-3 h-3" /> : null}{meta.label}
                          </span>
                          {g.category === "redundant" && g.loser_ids.length > 0 && (
                            <Button size="sm" data-testid={`cancel-group-${gi}`} disabled={busyGroup === gi}
                              onClick={() => cancelGroup(g, gi)}
                              className="bg-rose-600 hover:bg-rose-500 text-white h-7 text-xs">
                              {busyGroup === gi ? "Cancelling…" : `Cancel ${g.loser_ids.length} unpaid`}
                            </Button>
                          )}
                        </div>
                      </div>
                      {g.category === "needs_review" && (
                        <p className="text-xs text-rose-300/80 mb-2">Flagged for manual review — more than one paid, or all unpaid. Not auto-picked.</p>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-slate-400 text-xs text-left">
                              <th className="py-1 pr-3">Reference</th>
                              <th className="py-1 pr-3">Status</th>
                              <th className="py-1 pr-3">Created</th>
                              <th className="py-1 pr-3 text-right">Amount</th>
                              <th className="py-1 pl-3">Role</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.transactions.map((t, j) => (
                              <tr key={j} className="border-t border-white/5">
                                <td className="py-1.5 pr-3 font-mono text-xs">{t.payment_reference || t.id.slice(0, 8) + "…"}</td>
                                <td className="py-1.5 pr-3">
                                  <span className={`text-xs px-2 py-0.5 rounded ${t.is_archived ? "bg-slate-700 text-slate-400" : t.status_bucket === "paid" ? "bg-emerald-500/20 text-emerald-300" : t.status_bucket === "unpaid" ? "bg-rose-500/20 text-rose-300" : "bg-slate-700 text-slate-300"}`}>
                                    {t.is_archived ? "archived" : t.status}
                                  </span>
                                </td>
                                <td className="py-1.5 pr-3 text-xs text-slate-400">{(t.created_at || "").slice(0, 10)}</td>
                                <td className="py-1.5 pr-3 text-right">{money(t.amount)}</td>
                                <td className="py-1.5 pl-3">
                                  {t.is_keeper && <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">KEEP</span>}
                                  {t.is_cancel_candidate && <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">cancel</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
